import type { SidebarMenuItem } from "@rc-acs/shared/ui";
import { Sidebar as SharedSidebar, useI18n } from "@rc-acs/shared/ui";
import {
  CreditCard,
  FileCode,
  Files,
  FileText,
  HardDrive,
  Layers,
  LayoutDashboard,
  Package,
  Radio,
  RefreshCw,
  Server,
  Settings,
  Shield,
  Sliders,
  Smartphone,
  Tags,
  Terminal,
  UserCheck,
  Users,
  Variable,
  Wifi,
  Wrench,
} from "lucide-react";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

interface CoreSidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

/** Recursively find the item whose href EXACTLY equals the pathname. */
function findExactMatch(items: SidebarMenuItem[], pathname: string): string | undefined {
  for (const item of items) {
    if (item.divider) continue;
    if (item.href && item.href === pathname) return item.id;
    if (item.children) {
      const childMatch = findExactMatch(item.children, pathname);
      if (childMatch) return childMatch;
    }
  }
  return undefined;
}

/** Recursively find the item whose href is the LONGEST path-prefix of the pathname. */
function findLongestPrefixMatch(items: SidebarMenuItem[], pathname: string): string | undefined {
  let bestId: string | undefined;
  let bestLen = -1;
  const walk = (list: SidebarMenuItem[]): void => {
    for (const item of list) {
      if (item.divider) continue;
      // Belt-and-braces: a "/" href must never prefix-match every route (dashboard owns "/"). Do not "clean up".
      if (item.href && item.href !== "/" && pathname.startsWith(`${item.href}/`) && item.href.length > bestLen) {
        bestLen = item.href.length;
        bestId = item.id;
      }
      if (item.children) walk(item.children);
    }
  };
  walk(items);
  return bestId;
}

/**
 * Recursively search for the active item ID matching a pathname.
 * Exact href match always wins over prefix matching, so flat sibling child items
 * (e.g. /admin/billing/contracts) stay active instead of the shorter parent href
 * (/admin/billing) that is merely a path prefix of them. When nothing matches
 * exactly (detail routes like /clients/42), the LONGEST prefix wins.
 */
function findActiveInItems(items: SidebarMenuItem[], pathname: string): string | undefined {
  return findExactMatch(items, pathname) ?? findLongestPrefixMatch(items, pathname);
}

function getActiveItemFromPath(pathname: string, menuItems: SidebarMenuItem[]): string | undefined {
  // Belt-and-braces counterpart of the href !== "/" guard: root always maps to dashboard. Do not "clean up".
  if (pathname === "/") return "dashboard";
  return findActiveInItems(menuItems, pathname);
}

export function Sidebar({ collapsed, onCollapse, mobileOpen, onMobileClose }: CoreSidebarProps) {
  const location = useLocation();
  const { t } = useI18n();

  const menuItems: SidebarMenuItem[] = useMemo(() => [
    { id: "dashboard", label: t("sidebar.dashboard"), icon: LayoutDashboard, href: "/" },
    { id: "engines", label: t("sidebar.engineRegistry"), icon: Server, href: "/engines" },
    {
      id: "engineering",
      label: t("sidebar.engineering"),
      icon: Wrench,
      children: [
        // TR-069 sub-group
        {
          id: "engineering-tr069",
          label: t("sidebar.engTr069"),
          icon: Radio,
          children: [
            { id: "presets", label: t("sidebar.engPresets"), icon: Wrench, href: "/engineering/tr069/presets" },
            { id: "provisions", label: t("sidebar.engProvisions"), icon: FileCode, href: "/engineering/tr069/provisions" },
            { id: "virtual-params", label: t("sidebar.engVirtualParams"), icon: Sliders, href: "/engineering/tr069/virtual-params" },
            { id: "cwmp-configs", label: t("sidebar.engCwmpConfigs"), icon: Terminal, href: "/engineering/tr069/cwmp-configs" },
            { id: "files", label: t("sidebar.engFiles"), icon: Files, href: "/engineering/tr069/files" },
          ],
        },
        // TR-369 sub-group (placeholder)
        {
          id: "engineering-tr369",
          label: t("sidebar.engTr369"),
          icon: Wifi,
          children: [
            { id: "tr369-controllers", label: t("sidebar.engTr369Controllers"), icon: Wifi, href: "/engineering/tr369/controllers" },
          ],
        },
        // Devices sub-group
        {
          id: "engineering-devices",
          label: t("sidebar.engDevices"),
          icon: Smartphone,
          children: [
            { id: "cpe-models", label: t("sidebar.engCpeModels"), icon: Smartphone, href: "/engineering/devices/cpe-models" },
            { id: "vendor-mappings", label: t("sidebar.engVendorMappings"), icon: Tags, href: "/engineering/devices/vendor-mappings" },
            { id: "firmware", label: t("sidebar.engFirmware"), icon: Package, href: "/engineering/devices/firmware" },
          ],
        },
        // OLT sub-group
        {
          id: "engineering-olt",
          label: t("sidebar.engOlt"),
          icon: Server,
          children: [
            { id: "olts", label: t("sidebar.engOltManagement"), icon: Server, href: "/engineering/olt" },
          ],
        },
        { id: "engineering-divider", divider: true },
        // Dynamic Vars (transversal, direct item)
        { id: "dynamic-vars", label: t("sidebar.engDynamicVars"), icon: Variable, href: "/engineering/dynamic-vars" },
        { id: "sync-status", label: t("sidebar.engSyncStatus"), icon: RefreshCw, href: "/engineering/sync-status" },
      ],
    },
    {
      id: "admin",
      label: t("sidebar.admin"),
      icon: Shield,
      children: [
        { id: "users", label: t("sidebar.adminUsers"), icon: Users, href: "/admin/users" },
        { id: "settings", label: t("sidebar.adminSettings"), icon: Settings, href: "/admin/settings" },
        { id: "backups", label: t("sidebar.adminBackups"), icon: HardDrive, href: "/admin/backups" },
        { id: "billing", label: t("sidebar.adminBilling"), icon: CreditCard, href: "/admin/billing" },
        { id: "billing-plans", label: t("sidebar.adminBillingPlans"), icon: Layers, href: "/admin/billing/plans" },
        { id: "billing-clients", label: t("sidebar.adminBillingClients"), icon: UserCheck, href: "/admin/billing/clients" },
        { id: "billing-contracts", label: t("sidebar.adminBillingContracts"), icon: FileText, href: "/admin/billing/contracts" },
      ],
    },
  ], [t]);

  const activeItemId = getActiveItemFromPath(location.pathname, menuItems);

  return (
    <SharedSidebar
      menuItems={menuItems}
      activeItemId={activeItemId}
      collapsed={collapsed}
      onCollapse={onCollapse}
      mobileOpen={mobileOpen}
      onMobileClose={onMobileClose}
    />
  );
}
